import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConversations } from '../../contexts/ConversationContext';
import { ConversationSession } from '../../types';
import './ConversationSidebar.css';

interface ConversationSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  currentSessionId: string | null;
  onSelectConversation: (sessionId: string) => void;
  onNewConversation: () => void;
  hasMessages: boolean;
}

const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  isOpen,
  onClose,
  currentSessionId,
  onSelectConversation,
  onNewConversation,
  hasMessages,
}) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { conversations, loading, error, deleteConversation } = useConversations();
  const [searchTerm, setSearchTerm] = useState('');

  const handleNewConversation = () => {
    if (hasMessages) {
      // Only create new conversation if current one has messages
      onNewConversation();
    }
  };

  const handleUserClick = () => {
    if (user?.is_admin) {
      navigate('/admin');
    }
  };

  const handleDeleteConversation = async (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    if (window.confirm('¿Estás seguro de que quieres eliminar esta conversación?')) {
      await deleteConversation(sessionId);
    }
  };

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (days === 0) {
      return date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } else if (days === 1) {
      return 'Ayer';
    } else if (days < 7) {
      return `Hace ${days} días`;
    } else {
      return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
    }
  };

  const getConversationPreview = (conversation: ConversationSession) => {
    const lastMessage = conversation.messages?.[conversation.messages.length - 1];
    if (!lastMessage) return 'Nueva conversación';
    
    const content = lastMessage.content;
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  };

  const getConversationOwnerLabel = (conversation: ConversationSession) => {
    const candidateEmail = conversation.email;

    if (typeof candidateEmail === 'string' && candidateEmail.trim()) {
      return candidateEmail.trim();
    }

    return 'Email no disponible';
  };

  const filteredConversations = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    if (!normalizedSearch) return conversations;

    return conversations.filter((conversation) => {
      const searchable = [
        getConversationOwnerLabel(conversation),
        conversation.user_id,
        conversation.session_id,
        conversation.channel,
        conversation.status,
        getConversationPreview(conversation),
      ]
        .map((value) => (value || '').toLowerCase());

      return searchable.some((value) => value.includes(normalizedSearch));
    });
  }, [conversations, searchTerm]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <div className="status-dot status-active"></div>;
      case 'completed':
        return <div className="status-dot status-completed"></div>;
      case 'paused':
        return <div className="status-dot status-paused"></div>;
      default:
        return <div className="status-dot status-unknown"></div>;
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose}></div>}
      
      <aside className={`conversation-sidebar ${isOpen ? 'open' : 'closed'}`}>
        {/* Header */}
        <div className="sidebar-header">
          <button 
            onClick={handleNewConversation} 
            className={`new-chat-btn ${!hasMessages ? 'disabled' : ''}`}
            disabled={!hasMessages}
            title={hasMessages ? 'Nueva conversación' : 'Escribe un mensaje primero'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>Nueva conversación</span>
          </button>
          
          <button
            onClick={onClose}
            className="sidebar-toggle-btn"
            title="Cerrar sidebar"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        {/* Conversation List */}
        <div className="sidebar-conversations">
          {loading ? (
            <div className="conversation-loading">
              <div className="spinner"></div>
              <span>Cargando conversaciones...</span>
            </div>
          ) : error ? (
            <div className="conversation-error">
              <p>{error}</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="conversation-empty">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <p>No hay conversaciones aún</p>
              <small>Inicia una nueva conversación para comenzar</small>
            </div>
          ) : user?.is_admin && filteredConversations.length === 0 ? (
            <>
              <div className="conversation-search-wrapper">
                <label htmlFor="sidebar-conversation-search" className="conversation-search-field">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="11" cy="11" r="8"></circle>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                  </svg>
                  <input
                    id="sidebar-conversation-search"
                    type="text"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Buscar por email o sesión"
                  />
                </label>
              </div>
              <div className="conversation-empty">
                <p>No se encontraron chats con ese filtro</p>
                <small>Intenta con email o session ID</small>
              </div>
            </>
          ) : (
            <>
              {user?.is_admin && (
                <div className="conversation-search-wrapper">
                  <label htmlFor="sidebar-conversation-search" className="conversation-search-field">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                    </svg>
                    <input
                      id="sidebar-conversation-search"
                      type="text"
                      value={searchTerm}
                      onChange={(event) => setSearchTerm(event.target.value)}
                      placeholder="Buscar por email o sesión"
                    />
                  </label>
                  <span className="conversation-search-count">
                    {filteredConversations.length}/{conversations.length}
                  </span>
                </div>
              )}
              {filteredConversations.map((conversation) => (
              <div
                key={conversation.session_id}
                className={`conversation-item ${
                  conversation.session_id === currentSessionId ? 'active' : ''
                }`}
                onClick={() => onSelectConversation(conversation.session_id)}
              >
                <div className="conversation-icon">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                  </svg>
                </div>
                
                <div className="conversation-content">
                  {user?.is_admin && (
                    <div className="conversation-owner">{getConversationOwnerLabel(conversation)}</div>
                  )}
                  <div className="conversation-header">
                    <span className="conversation-preview">
                      {getConversationPreview(conversation)}
                    </span>
                    <span className="conversation-time">
                      {formatDate(conversation.updated_at)}
                    </span>
                  </div>
                  
                  <div className="conversation-meta">
                    <div className="conversation-status">
                      {getStatusIcon(conversation.status)}
                      <span>{conversation.message_count} mensajes</span>
                    </div>
                    
                    <button
                      onClick={(e) => handleDeleteConversation(e, conversation.session_id)}
                      className="delete-conversation-btn"
                      title="Eliminar conversación"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="3,6 5,6 21,6"></polyline>
                        <path d="M19,6v14a2,2 0 0,1-2,2H7a2,2 0 0,1-2-2V6m3,0V4a2,2 0 0,1,2-2h4a2,2 0 0,1,2,2v2"></path>
                        <line x1="10" y1="11" x2="10" y2="17"></line>
                        <line x1="14" y1="11" x2="14" y2="17"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              ))}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-user" onClick={handleUserClick}>
            <div className={`user-avatar ${user?.is_admin ? 'admin' : ''}`}>
              {(user?.email || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="user-info">
              <span className="user-display-name">{user?.email}</span>
              {user?.is_admin && (
                <span className="user-role">Administrador</span>
              )}
            </div>
            {user?.is_admin && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12"></line>
                <polyline points="12 5 19 12 12 19"></polyline>
              </svg>
            )}
          </div>
          
          <button onClick={logout} className="logout-btn" title="Cerrar sesión">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
              <polyline points="16 17 21 12 16 7"></polyline>
              <line x1="21" y1="12" x2="9" y2="12"></line>
            </svg>
          </button>
        </div>
      </aside>
    </>
  );
};

export default ConversationSidebar;